import { px } from '@adsk/alloy-react-helpers';

import {
  AlloyLegacyThemeDesignTokens,
  DraggableItemDesignTokens,
} from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

export const getDraggableItemTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
  transitionDuration: number,
  transitionEasing: string,
): DraggableItemDesignTokens => ({
  'background-color': theme.colors.white,
  'border-width': px(1),
  'border-style': 'solid',
  'border-radius': px(theme.borderRadius.medium),
  'min-height': px(40),
  gap: px(theme.spacing.XXS),
  'padding-top': px(theme.spacing.XS),
  'padding-right': px(theme.spacing.XS),
  'padding-bottom': px(theme.spacing.XS),
  'padding-left': px(theme.spacing.XXS),
  'padding-left-no-icon': px(theme.spacing.XS),
  transition: theme.transitions.create(
    [
      'width',
      'opacity',
      'gap',
      'box-shadow',
      'border-color',
      'background-color',
    ],
    { duration: transitionDuration, easing: transitionEasing },
  ),
  'border-color': {
    default: theme.colors.charcoal200,
    hover: theme.colors.charcoal400,
  },
  shadow: {
    hover: `0 0 0 2px ${theme.colors.charcoal200}`,
    dragging: theme.shadows.highElevation,
  },
  icon: {
    size: 24,
    'icon-color': theme.colors.charcoal700,
    transition: theme.transitions.create(['width', 'opacity'], {
      duration: transitionDuration,
      easing: transitionEasing,
    }),
  },
  content: {
    color: theme.tokens.colors.text.primary.value,
    'font-family': theme.typography.labelMedium.fontFamily,
    'font-size': theme.typography.labelMedium.fontSize,
    'font-weight': theme.typography.labelMedium.fontWeight,
    'line-height': theme.typography.labelMedium.lineHeight,
  },
  opacity: {
    disabled: 0.4,
  },
  cursor: {
    default: 'grab',
    disabled: 'not-allowed',
    dragging: 'grabbing',
  },
  'drop-target': {
    'border-color': theme.colors.adskBlue500,
    'background-color': theme.tokens.colors.background.secondary.value,
  },
});

export const getDraggableItemTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): DraggableItemDesignTokens => {
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;

  const spacing4 = generic?.spacing?.[4];
  const spacing8 = generic?.spacing?.[8];

  const transitionDuration = generic?.['transition-duration']?.[200];
  const transitionEasing = generic?.['transition-timing-function']?.cubic;
  const transitionTiming =
    transitionDuration !== undefined && transitionEasing !== undefined
      ? `${transitionDuration} ${transitionEasing}`
      : undefined;
  const composeTransition = (properties: string[]): string | undefined =>
    transitionTiming
      ? properties
          .map((property) => `${property} ${transitionTiming}`)
          .join(', ')
      : undefined;

  const hoverRingOffset = generic?.shadow?.hard?.[2];
  const hoverRingColor = semantic?.['border-color']?.divider?.light;

  return {
    'background-color': semantic?.['background-color']?.surface?.[100],
    'border-width': generic?.['border-width']?.[1],
    'border-style': 'solid',
    'border-radius': generic?.['border-radius']?.[4],
    'min-height': generic?.size?.[44],
    gap: spacing4,
    'padding-top': spacing8,
    'padding-right': spacing8,
    'padding-bottom': spacing8,
    'padding-left': spacing4,
    'padding-left-no-icon': spacing8,
    transition: composeTransition([
      'width',
      'opacity',
      'gap',
      'box-shadow',
      'border-color',
      'background-color',
    ]),
    'border-color': {
      default: semantic?.['border-color']?.divider?.light,
      hover: semantic?.['border-color']?.medium?.default,
    },
    shadow: {
      hover:
        hoverRingOffset && hoverRingColor
          ? `${hoverRingOffset} ${hoverRingColor}`
          : undefined,
      dragging: semantic?.shadow?.high,
    },
    icon: {
      size: generic?.size?.[24],
      'icon-color': semantic?.['icon-color']?.medium,
      transition: composeTransition(['width', 'opacity']),
    },
    content: {
      color: semantic?.['text-color']?.medium?.default,
      'font-family': fontFamily,
      'font-size': generic?.['font-size']?.[14],
      'font-weight': '600',
      'line-height': generic?.['line-height']?.[20],
    },
    opacity: {
      disabled: semantic?.opacity?.disabled,
    },
    cursor: {
      default: 'grab',
      disabled: 'not-allowed',
      dragging: 'grabbing',
    },
    'drop-target': {
      'border-color': semantic?.['border-color']?.selected,
      'background-color': semantic?.['background-color']?.surface?.[200],
    },
  };
};
